import React, { useState, useEffect } from "react";
import "./04_Sinus_und_Kosinus.css";
import TopicFrame from "../../../components/TopicFrame/TopicFrame";
import Subtopic from "../../../components/Subtopic/Subtopic";
import RangeSlider from "../../../components/RangeSlider/RangeSlider";

const SigaPage = () => {
  const [phi, setPhi] = useState(180); // Initialwert für phi

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.11.3/p5.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const p5 = window.p5;

      let style = getComputedStyle(document.body);
      let AccentColor = style.getPropertyValue("--AccentColor").trim();
      let AccentColor80Alpha = AccentColor.replace("rgb", "rgba").replace(")", `, 0.8)`);

      let slider = document.getElementById("phiInput");

      const containerEinheitskreis = document.getElementById("canvasContainerEinheitskreis");
      const containerFunktionen = document.getElementById("canvasContainerFunktionen");

      let scale;
      let amplitude;
      let radius;
      let minX, minY, centerX, centerY, maxX, maxY;
      let border = 10;

      let phi;

      let w;
      let sinusAmplituden = [];
      let cosinusAmplituden = [];
      let xPos, yPos;
      let angleStart, angleEnd;

      const drawStroke_gray = (p) => p.stroke(150);
      const drawStroke_darkGray = (p) => p.stroke(50);
      const drawStroke_black = (p) => p.stroke(0);

      calculateScale();
      window.addEventListener("resize", calculateScale);

      function calculateScale() {
        const containerWidth = containerEinheitskreis.getBoundingClientRect().width;
        scale = containerWidth;

        amplitude = scale / 2 - border / 2;
        radius = scale * 0.8 * 0.5;

        minX = 0;
        minY = 0;
        centerX = scale / 2;
        centerY = scale / 2;
        maxX = scale;
        maxY = scale;
      }

      function sinDegrees(angleDegrees) {
        return Math.sin((angleDegrees * Math.PI) / 180);
      }

      function cosDegrees(angleDegrees) {
        return Math.cos((angleDegrees * Math.PI) / 180);
      }

      let sketch1 = function (p) {
        p.setup = function () {
          p.createCanvas(scale, scale).parent(containerEinheitskreis);

          slider.value = phi;
          slider.addEventListener("input", p.updatePhi);

          p.updatePhi();

          // TEXT
          p.textSize(12); // Setzt die Textgröße
          p.textStyle(p.NORMAL);
          p.textAlign(p.CENTER, p.CENTER);
        };

        p.draw = function () {
          p.resizeCanvas(scale, scale);

          angleEnd = 0;
          angleStart = angleEnd - p.radians(phi);
          xPos = centerX + cosDegrees(phi) * radius;
          yPos = centerY - sinDegrees(phi) * radius;

          drawStroke_black(p);
          p.strokeWeight(1);
          p.background(255);
          p.fill(255);

          // Circle
          p.circle(centerX, centerY, radius * 2);

          // Axes
          p.stroke(200);
          p.line(minX, centerY, maxX, centerY);
          p.line(centerX, minY, centerX, maxY);

          // Arc
          drawStroke_black(p);
          p.fill(AccentColor80Alpha);
          p.arc(centerX, centerY, 0.3 * scale, 0.3 * scale, angleStart, angleEnd);

          //
          p.line(centerX, centerY, scale / 2 + 0.15 * scale, scale / 2);
          drawStroke_black(p);
          p.strokeWeight(2);
          p.line(centerX, yPos, xPos, yPos);
          drawStroke_black(p);
          p.strokeWeight(2);
          p.line(xPos, centerY, xPos, yPos);
          drawStroke_black(p);
          p.strokeWeight(1);
          p.fill(112, 0, 0);
          p.line(centerX, centerY, xPos, yPos);
          p.circle(xPos, yPos, scale * 0.02);

          const labels = [
            { angle: 0, text: "0° \n(360°)", xOffset: 0.45 * scale, yOffset: 0 },
            { angle: 90, text: "90°", xOffset: 0, yOffset: -0.45 * scale },
            { angle: 180, text: "180°", xOffset: -0.45 * scale, yOffset: 0 },
            { angle: 270, text: "270°", xOffset: 0, yOffset: 0.45 * scale },
          ];

          let phiMod = phi % 360;

          for (const label of labels) {
            let color = label.angle === phiMod ? AccentColor : [0];
            p.stroke(color);
            p.fill(color);
            p.text(label.text, centerX + label.xOffset, centerY + label.yOffset);
          }
        };

        p.updatePhi = function () {
          phi = parseFloat(slider.value);

          document.getElementById("sin").innerText = sinDegrees(phi).toFixed(2);
          document.getElementById("cos").innerText = cosDegrees(phi).toFixed(2);
          document.getElementById("phi").innerHTML = phi.toFixed(0) + "°";
        };
      };

      let sketch2 = function (p) {
        p.setup = function () {
          p.textAlign(p.CENTER, p.CENTER);
          p.createCanvas(scale, scale).parent(containerFunktionen);

          const period = 2 * p.PI;

          for (let i = 0; i <= p.width - border; i++) {
            const t = (i / p.width - border) * period;
            sinusAmplituden[i] = Math.sin(t) * amplitude;
            cosinusAmplituden[i] = Math.cos(t) * amplitude;
          }
        };

        p.draw = function () {
          calculateScale();
          p.background(255);
          let quadratSize = p.width - border; // Berechnung der Quadratgröße

          p.noStroke();
          p.fill(200, 200, 255); // Setzt die Füllfarbe auf Blau
          p.rect(border / 2, border / 2, quadratSize, quadratSize); // Quadrat zeichnen

          p.translate(border / 2, p.height / 2); // Verschiebung des Koordinatensystems

          // Koordinatensystem zeichnen
          drawStroke_gray(p);
          p.line(0, 0, p.width, 0); // x-Achse
          p.line(0, -p.height / 2, 0, p.height / 2); // y-Achse

          // Linien entlang der y-Achse alle 30 Pixel
          drawStroke_gray(p);
          for (let y = 0; y <= (p.height - border) / 2; y += (p.height - border) / 20) {
            p.line(-5, y, 5, y);
            p.line(-5, -y, 5, -y);
          }

          // Linien entlang der x-Achse alle PI/8 und Beschriftungen hinzufügen
          for (let x = 0; x < p.width - border; x += (p.width - border) / 8) {
            p.line(x, -5, x, 5);
            p.fill(0); // Schwarze Farbe für den Text
            if (p.abs(x - (w * p.PI) / 2) < 1) {
              p.text("π/2", x, 20);
            } else if (p.abs(x - w * p.PI) < 1) {
              p.text("π", x, 20);
            } else if (p.abs(x - (w * 3 * p.PI) / 2) < 1) {
              p.text("3π/2", x, 20);
            } else if (p.abs(x - w * 2 * p.PI) < 1) {
              p.text("2π", x, 20);
            }
          }

          // Sinuskurve zeichnen
          drawStroke_darkGray(p);
          p.noFill();
          p.beginShape();
          for (let i = 0; i < p.width; i++) {
            p.vertex(i, sinusAmplituden[i]);
          }
          p.endShape();

          // Cosinuskurve zeichnen
          p.stroke(AccentColor80Alpha);
          p.beginShape();
          for (let i = 0; i < p.width; i++) {
            p.vertex(i, cosinusAmplituden[i]);
          }
          p.endShape();

          // Punkt auf Sinus- und Cosinus-Kurve zeichnen basierend auf dem Slider-Wert
          let sliderVal = parseInt(slider.value);
          let mappedValue = Math.round(p.map(sliderVal, 0, 360, 0, p.width));

          // Linien an Sinus-Kurve
          p.stroke(50, 50, 50, 100);
          p.line(0, sinusAmplituden[mappedValue], mappedValue, sinusAmplituden[mappedValue]); // Linie zur y-Achse
          p.line(mappedValue, 0, mappedValue, sinusAmplituden[mappedValue]); // Linie zur x-Achse

          // Linien an Cosinus-Kurve
          p.stroke(AccentColor80Alpha);
          p.line(0, cosinusAmplituden[mappedValue], mappedValue, cosinusAmplituden[mappedValue]); // Linie zur y-Achse
          p.line(mappedValue, 0, mappedValue, cosinusAmplituden[mappedValue]); // Linie zur x-Achse

          // Punkte auf Kurven
          drawStroke_darkGray(p);
          p.fill(50);
          p.circle(mappedValue, sinusAmplituden[mappedValue], border - 2);
          p.stroke(AccentColor80Alpha);
          p.fill(AccentColor80Alpha);
          p.circle(mappedValue, cosinusAmplituden[mappedValue], border - 2);

          // Beschriftungen für die y-Achse hinzufügen
          p.fill(0);
          p.noStroke();
          p.text("1", 10, -(amplitude - 5));
          p.text("0", 10, 5);
          p.text("-1", 10, amplitude - 5);
        };
      };

      new p5(sketch1);
      new p5(sketch2);
    };

    // Cleanup: Entfernen des Skripts, wenn die Komponente unmontiert wird
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <TopicFrame topicType="signalTheorie" entryNumber={3}>
        <Subtopic Title="Sinus und Kosinus am Einheitskreis" id="SinusUndKosinusAmEinheitskreis">
          <div className="sinCosCanvasContainer">
            <div id="canvasContainerEinheitskreis"></div>
            <div id="canvasContainerFunktionen"></div>
          </div>
          <div className="tableContainer">
            <div className="column">
              <div className="H6" id="sinLabel">
                SIN
              </div>
              <p className="H3" id="sin" />
            </div>
            <div className="column">
              <p className="H5" id="phiLabel">
                &phi;
              </p>
              <p className="H3" id="phi" />
            </div>
            <div className="column">
              <div className="H6" id="cosLabel">
                COS
              </div>
              <p className="H3" id="cos" />
            </div>
          </div>
          <RangeSlider min={0} max={360} step={1} value={phi} onChange={(e) => setPhi(e.target.value)} id="phiInput" />
        </Subtopic>
      </TopicFrame>
    </>
  );
};

export default SigaPage;
